import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Translation, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { toAdministrationUser } from '@modules/administration/adapters/administration-user.adapter';
import { AdministrationUser } from '@modules/administration/models/administration-user';
import { UserAdminState } from '@modules/administration/models/user-admin-state.enum';
import { Store } from '@ngrx/store';
import { DepartmentMembershipData, writeAccessLicenseStates } from '@pixacare/domain';
import { toClientUser } from '@pixacare/dtos';
import { formatUsername, isNullOrEmpty } from '@pixacare/shared';
import { DepartmentHttpService } from '@services/http/department.http.service';
import { RequestResult } from '@shared/request-state/models/request-result';
import { joinRequestResult } from '@shared/request-state/rx-pipes/join-request-result.pipe';
import { trackRequestResult } from '@shared/request-state/rx-pipes/track-request-result.pipe';
import { usersActions } from '@shared/store/users/users.actions';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { departmentsActions } from 'src/app/shared/store/departments/departments.actions';
import {
  selectDepartmentMemberships,
  selectUserDepartmentIds,
} from 'src/app/shared/store/departments/departments.selectors';
import { selectDepartmentUsers } from 'src/app/shared/store/users/users.selectors';
import { ClientAddUserComponent } from '../../../dashboard/client-add-user/client-add-user.component';
import { DepartmentAdminService } from '../department-admin/department-admin.service';
import { departmentUsersAdminActions } from './effects/department-users-admin.actions';

const enableUserDialogOptions = {
  title: marker('Êtes-vous certain de vouloir ajouter {{ userName }} au groupe ?'),
  confirmLabel: marker(`Ajouter l'utilisateur`),
};

const disableUserDialogOptions = {
  title: marker('Êtes-vous certain de vouloir retirer {{ userName }} au groupe ?'),
  confirmLabel: marker(`Retirer l'utilisateur`),
};

const removeInvitedUserDialogOptions = {
  title: marker('Êtes-vous certain de vouloir retirer {{ userDisplay }} de ce groupe ?'),
  confirmLabel: marker(`Retirer l'utilisateur`),
};

@Injectable()
export class DepartmentUsersAdminService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly departmentAdminService = inject(DepartmentAdminService);
  private readonly departmentHttpService = inject(DepartmentHttpService);
  private readonly translocoService = inject(TranslocoService);

  readonly departmentUsers$: Observable<RequestResult<AdministrationUser[]>> = this.departmentAdminService.params$.pipe(
    switchMap(({ clientCode, departmentId }) =>
      combineLatest([
        this.store.select(selectDepartmentUsers(clientCode, departmentId)),
        this.store.select(selectDepartmentMemberships(clientCode, departmentId)),
      ]).pipe(
        joinRequestResult(this.trackingKey(clientCode, departmentId)),
        map((state) => {
          if (!state.isSuccess) {
            return {
              ...state,
              data: [],
            };
          }

          const [users, memberships] = state.data;

          return {
            ...state,
            data: users.map((clientUser) => toAdministrationUser(clientUser, memberships[clientUser.id])),
          };
        }),
      ),
    ),
  );

  init(): void {
    this.departmentAdminService.params$
      .pipe(
        filter(({ clientCode, departmentId }) => !!clientCode && !!departmentId),
        distinctUntilChanged(
          (prev, curr) => prev.clientCode === curr.clientCode && prev.departmentId === curr.departmentId,
        ),
        switchMap(({ clientCode, departmentId }) =>
          this.departmentHttpService.getDepartmentUsers(clientCode, departmentId).pipe(
            tap((users) => {
              this.store.dispatch(
                departmentsActions.updateDepartmentMembershipsSuccess({
                  clientCode,
                  departmentId,
                  memberships: users.map((user) => ({
                    ...user.membership,
                    hasActiveLicense: writeAccessLicenseStates.includes(user.licenseState),
                  })),
                }),
              );
              this.store.dispatch(
                usersActions.addUsersSuccess({
                  clientUsers: users.map((user) => toClientUser(user)),
                }),
              );
            }),
            trackRequestResult(this.trackingKey(clientCode, departmentId)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  enableUser(user: AdministrationUser): void {
    this.openConfirmDialog(enableUserDialogOptions, { userName: formatUsername(user) })
      .pipe(first())
      .subscribe({
        next: ({ clientCode, departmentId }) => {
          this.updateMembership(clientCode, departmentId, user.id, {
            isAdmin: user.isAdmin,
            disabledOn: null,
          });
        },
      });
  }

  disableUser(user: AdministrationUser): void {
    this.openConfirmDialog(disableUserDialogOptions, { userName: formatUsername(user) })
      .pipe(first())
      .subscribe({
        next: ({ clientCode, departmentId }) => {
          this.updateMembership(clientCode, departmentId, user.id, {
            isAdmin: user.isAdmin,
            disabledOn: new Date(),
          });
        },
      });
  }

  adminChange(user: AdministrationUser): void {
    this.departmentAdminService.params$
      .pipe(
        switchMap(({ clientCode, departmentId }) =>
          this.store
            .select(selectDepartmentMemberships(clientCode, departmentId))
            .pipe(map((memberships) => ({ clientCode, departmentId, memberships }))),
        ),
        first(),
      )
      .subscribe(({ clientCode, departmentId, memberships }) => {
        this.updateMembership(clientCode, departmentId, user.id, {
          isAdmin: !user.isAdmin,
          disabledOn: memberships[user.id]?.disabledOn,
        });
      });
  }

  deleteInvitedUser(user: AdministrationUser): void {
    this.openConfirmDialog(removeInvitedUserDialogOptions, {
      userDisplay: user.state === UserAdminState.INVITED ? user.mailAddress : formatUsername(user),
    })
      .pipe(
        switchMap(({ clientCode }) =>
          this.store
            .select(selectUserDepartmentIds(clientCode, user.id))
            .pipe(map((departmentIds) => ({ clientCode, departmentIds }))),
        ),
        first(),
      )
      .subscribe({
        next: ({ clientCode, departmentIds }) => {
          this.store.dispatch(
            departmentUsersAdminActions.deleteInvitedUser({
              clientCode,
              userId: user.id,
              licenseId: user.licenseId,
              departmentIds,
            }),
          );
        },
      });
  }

  addUsers(): void {
    this.dialogService
      .openComponent<ClientAddUserComponent, string[]>(ClientAddUserComponent, {
        label: this.translocoService.translate('Ajouter des utilisateurs'),
      })
      .pipe(
        first((mailAddresses) => !isNullOrEmpty(mailAddresses)),
        withLatestFrom(this.departmentAdminService.params$),
      )
      .subscribe({
        next: ([mailAddresses, { clientCode, departmentId }]) => {
          this.store.dispatch(
            departmentUsersAdminActions.addUsers({
              clientCode,
              departmentId,
              mailAddresses,
            }),
          );
        },
      });
  }

  private openConfirmDialog(
    { title, confirmLabel }: { title: string; confirmLabel: string },
    translateOptions: Translation,
  ): Observable<{ clientCode: string; departmentId: number }> {
    return this.dialogService
      .openConfirm(this.translocoService.translate(title, translateOptions), {
        yes: this.translocoService.translate(confirmLabel),
      })
      .pipe(
        first(Boolean),
        switchMap(() => this.departmentAdminService.params$),
      );
  }

  private updateMembership(
    clientCode: string,
    departmentId: number,
    departmentUserId: number,
    membership: DepartmentMembershipData,
  ): void {
    this.store.dispatch(
      departmentsActions.updateDepartmentUser({
        clientCode,
        departmentId,
        departmentUserId,
        membership,
      }),
    );
  }

  private trackingKey(clientCode: string, departmentId: number): string[] {
    return ['department-users', clientCode, departmentId + ''];
  }
}
